import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocsFromCache,
  getDocsFromServer,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import { UserNotificationConverter } from '@core/services/user-notifications/converters/user-notification-converter'
import { UserNotificationsErrorConverter } from '@core/services/user-notifications/converters/user-notifications-error-converter'
import type { UserNotification } from '@domain/models/user-notifications'

const USERS_COLLECTION = 'users'
const NOTIFICATIONS_SUBCOLLECTION = 'notifications'

function getUserNotificationsCollection(userId: string) {
  if (!firestore) {
    throw UserNotificationsErrorConverter.domainModel(
      new Error('Firestore is not initialized'),
    )
  }
  return collection(firestore, USERS_COLLECTION, userId, NOTIFICATIONS_SUBCOLLECTION)
}

function getUserNotificationDoc(userId: string, notificationId: string) {
  return doc(getUserNotificationsCollection(userId), notificationId)
}

function buildUserNotificationsQuery(userId: string) {
  const constraints: QueryConstraint[] = [orderBy('created_time', 'desc')]
  return query(getUserNotificationsCollection(userId), ...constraints)
}

function buildUnreadNotificationsQuery(userId: string) {
  return query(
    getUserNotificationsCollection(userId),
    where('read', '==', false),
  )
}

export interface CreateUserNotificationParams {
  forUserId: string
  notification: Partial<UserNotification>
}

export class UserNotificationsService {
  private static instance: UserNotificationsService

  private constructor() {}

  public static getInstance(): UserNotificationsService {
    if (!UserNotificationsService.instance) {
      UserNotificationsService.instance = new UserNotificationsService()
    }
    return UserNotificationsService.instance
  }

  public async fetchUserNotifications(userId: string): Promise<UserNotification[]> {
    try {
      const snapshot = await getDocsFromServer(buildUserNotificationsQuery(userId))
      return snapshot.docs.map((item) =>
        UserNotificationConverter.fromFirestore(item.id, item.data() as Record<string, unknown>),
      )
    } catch (error) {
      throw UserNotificationsErrorConverter.domainModel(error)
    }
  }

  public async loadUserNotifications(userId: string): Promise<UserNotification[]> {
    try {
      const snapshot = await getDocsFromCache(buildUserNotificationsQuery(userId))
      return snapshot.docs.map((item) =>
        UserNotificationConverter.fromFirestore(item.id, item.data() as Record<string, unknown>),
      )
    } catch (error) {
      throw UserNotificationsErrorConverter.domainModel(error)
    }
  }

  public observeUserNotifications(
    userId: string,
    callback: (notifications: UserNotification[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return onSnapshot(
      buildUserNotificationsQuery(userId),
      (snapshot) => {
        callback(
          snapshot.docs.map((item) =>
            UserNotificationConverter.fromFirestore(
              item.id,
              item.data() as Record<string, unknown>,
            ),
          ),
        )
      },
      (error) => {
        onError?.(UserNotificationsErrorConverter.domainModel(error))
      },
    )
  }

  public async createNotification(params: CreateUserNotificationParams): Promise<string> {
    try {
      const payload = UserNotificationConverter.toFirestore({
        ...params.notification,
        createdTime: params.notification.createdTime ?? new Date(),
        read: params.notification.read ?? false,
      })
      const createdDoc = await addDoc(getUserNotificationsCollection(params.forUserId), payload)
      return createdDoc.id
    } catch (error) {
      throw UserNotificationsErrorConverter.domainModel(error)
    }
  }

  public async deleteNotification(userId: string, notificationId: string): Promise<void> {
    try {
      await deleteDoc(getUserNotificationDoc(userId, notificationId))
    } catch (error) {
      throw UserNotificationsErrorConverter.domainModel(error)
    }
  }

  public async markNotificationAsRead(
    userId: string,
    notificationId: string,
    readTime = new Date(),
  ): Promise<void> {
    try {
      await updateDoc(getUserNotificationDoc(userId, notificationId), {
        read: true,
        read_time: readTime,
      })
    } catch (error) {
      throw UserNotificationsErrorConverter.domainModel(error)
    }
  }

  public async getUnreadNotificationsCount(userId: string): Promise<number> {
    try {
      const snapshot = await getCountFromServer(buildUnreadNotificationsQuery(userId))
      return snapshot.data().count
    } catch (error) {
      throw UserNotificationsErrorConverter.domainModel(error)
    }
  }

  public observeUnreadNotificationsCount(
    userId: string,
    callback: (count: number) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return onSnapshot(
      buildUnreadNotificationsQuery(userId),
      (snapshot) => {
        callback(snapshot.size)
      },
      (error) => {
        onError?.(UserNotificationsErrorConverter.domainModel(error))
      },
    )
  }
}

const userNotificationsService = UserNotificationsService.getInstance()

export async function fetchUserNotifications(userId: string): Promise<UserNotification[]> {
  return userNotificationsService.fetchUserNotifications(userId)
}

export async function loadUserNotifications(userId: string): Promise<UserNotification[]> {
  return userNotificationsService.loadUserNotifications(userId)
}

export function observeUserNotifications(
  userId: string,
  callback: (notifications: UserNotification[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return userNotificationsService.observeUserNotifications(userId, callback, onError)
}

export async function createNotification(
  params: CreateUserNotificationParams,
): Promise<string> {
  return userNotificationsService.createNotification(params)
}

export async function deleteNotification(userId: string, notificationId: string): Promise<void> {
  return userNotificationsService.deleteNotification(userId, notificationId)
}

export async function markNotificationAsRead(
  userId: string,
  notificationId: string,
  readTime = new Date(),
): Promise<void> {
  return userNotificationsService.markNotificationAsRead(userId, notificationId, readTime)
}

export async function getUnreadNotificationsCount(userId: string): Promise<number> {
  return userNotificationsService.getUnreadNotificationsCount(userId)
}

export function observeUnreadNotificationsCount(
  userId: string,
  callback: (count: number) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return userNotificationsService.observeUnreadNotificationsCount(userId, callback, onError)
}
